import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe, ImageIcon, Loader2, Save, ShoppingBag, Upload } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
    BENEFIT_ICONS,
    BENEFIT_ICON_OPTIONS,
    type BenefitIconName,
} from '@/app/components/settings/benefit-icon-catalog'

import { PurchaseBenefitsSettingsCard } from '@/app/components/settings/PurchaseBenefitsSettingsCard'
import { Button } from '@/app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import { Input } from '@/app/components/ui/input'
import {
    settingsRecord,
    settingsService,
    settingValue,
    type ImageSettingValue,
} from '@/app/services/settings.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const STORE_IMAGE_KEYS = [
    { key: 'store.logo_header', label: 'Logo header tienda' },
    { key: 'store.logo_footer', label: 'Logo footer tienda' },
    { key: 'store.favicon', label: 'Ícono de la tienda' },
] as const

type StoreImageKey = typeof STORE_IMAGE_KEYS[number]['key']

const DEFAULT_PURCHASE_BENEFITS: PurchaseBenefitsFormState = {
    title: 'Compra en línea y recibe en casa',
    subtitle: 'Todo lo que necesitas para tu salud, sin salir de casa',
    blocks: [
        {
            icon: 'shield-check',
            title: 'Compra segura',
            description: 'Tus datos y pago están protegidos con cifrado de nivel bancario.',
        },
        {
            icon: 'truck',
            title: 'Entrega a domicilio',
            description: 'Recibe tus productos en 30–45 minutos sin salir de casa.',
        },
        {
            icon: 'smartphone',
            title: 'Desde cualquier dispositivo',
            description: 'Navega y compra desde tu celular, tablet o computadora.',
        },
        {
            icon: 'message-circle',
            title: 'Atención personalizada',
            description: 'Nuestro equipo resuelve tus dudas los 7 días de la semana.',
        },
    ],
}

interface FormState {
    'store.slogan': string
    'store.top_bar_message': string
    'store.social.facebook': string
    'store.social.instagram': string
    'store.social.tiktok': string
    'store.social.whatsapp': string
    'store.purchase_benefits': PurchaseBenefitsFormState
}

const DEFAULT_FORM: FormState = {
    'store.slogan': '',
    'store.top_bar_message': '',
    'store.social.facebook': '',
    'store.social.instagram': '',
    'store.social.tiktok': '',
    'store.social.whatsapp': '',
    'store.purchase_benefits': DEFAULT_PURCHASE_BENEFITS,
}

interface PurchaseBenefitBlock {
    icon: BenefitIconName
    title: string
    description: string
}

interface PurchaseBenefitsFormState {
    title: string
    subtitle: string
    blocks: PurchaseBenefitBlock[]
}

export function StoreSettings() {
    const queryClient = useQueryClient()
    const canEditPlatform = useSessionStore((state) => state.canAction('configuration.configuration.edit'))
    const [form, setForm] = useState<FormState>(DEFAULT_FORM)
    const [savedForm, setSavedForm] = useState<FormState>(DEFAULT_FORM)
    const [uploadingKey, setUploadingKey] = useState<StoreImageKey | null>(null)

    const { data: settings = [], isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: settingsService.getSettings,
    })

    const settingMap = useMemo(() => settingsRecord(settings), [settings])

    useEffect(() => {
        if (!settings.length) {
            return
        }

        const nextForm: FormState = {
            'store.slogan': settingValue(settings, 'store.slogan', '') || '',
            'store.top_bar_message': settingValue(
                settings,
                'store.top_bar_message',
                'Entrega a domicilio en 30-45 min · Genéricos de calidad al mejor precio',
            ) || '',
            'store.social.facebook': settingValue(settings, 'store.social.facebook', '') || '',
            'store.social.instagram': settingValue(settings, 'store.social.instagram', '') || '',
            'store.social.tiktok': settingValue(settings, 'store.social.tiktok', '') || '',
            'store.social.whatsapp': settingValue(settings, 'store.social.whatsapp', '') || '',
            'store.purchase_benefits':
                settingValue(
                    settings,
                    'store.purchase_benefits',
                    DEFAULT_PURCHASE_BENEFITS,
                ) || DEFAULT_PURCHASE_BENEFITS,
        }

        setForm(nextForm)
        setSavedForm(nextForm)
    }, [settings])

    const updateMutation = useMutation({
        mutationFn: settingsService.updatePlatformSettings,
        onSuccess: (result) => {
            queryClient.setQueryData(['settings'], result.settings)
            toast.success('Configuracion actualizada')
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || 'No se pudo guardar la configuracion'
            toast.error(msg)
        },
    })

    const uploadMutation = useMutation({
        mutationFn: ({ key, file }: { key: StoreImageKey; file: File }) => settingsService.uploadAsset(key, file),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Imagen actualizada')
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || 'No se pudo subir la imagen'
            toast.error(msg)
        },
        onSettled: () => setUploadingKey(null),
    })

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!canEditPlatform) {
            toast.error('No tienes permiso para editar la configuración')
            return
        }

        const changedSettingsPayload = (current: any, original: any) => {
            const changed: Record<string, string> = {}
            for (const key in current) {
                if (current[key] !== original[key]) {
                    changed[key] = current[key]
                }
            }
            return changed
        }

        const settingsPayload = changedSettingsPayload(form, savedForm)
        console.log(form)

        if (Object.keys(settingsPayload).length === 0) {
            toast.info('No hay cambios por guardar')
            return
        }

        try {
            await updateMutation.mutateAsync({ settings: settingsPayload })
            setSavedForm(form)
        } catch {
            // Error manejado en mutation
        }
    }

    const handleImageChange = async (key: StoreImageKey, file?: File) => {
        if (!canEditPlatform || !file) {
            return
        }

        setUploadingKey(key)

        try {
            await uploadMutation.mutateAsync({ key, file })
        } catch {
            // Error manejado en mutation
        }
    }

    return (
        <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold text-foreground">Configuración de tienda en línea</h1>
                    <p className="text-sm text-muted-foreground">
                        Ajusta la identidad visual y las redes sociales de tu sitio público.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex min-h-64 items-center justify-center rounded-lg border border-card-border bg-card">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <Card className="rounded-lg">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <ShoppingBag className="h-5 w-5 text-primary" />
                                    Identidad de tienda
                                </CardTitle>
                                <CardDescription>Identidad y recursos visuales de la tienda.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                {/* Slogan */}
                                <div className="space-y-2">
                                    <label htmlFor="store_slogan" className="text-sm font-medium text-card-foreground">
                                        Slogan (aparece en el título de la página)
                                    </label>
                                    <Input
                                        id="store_slogan"
                                        value={form['store.slogan']}
                                        onChange={(e) => setForm((c) => ({ ...c, 'store.slogan': e.target.value }))}
                                        placeholder="Tu farmacia de confianza"
                                    />
                                </div>

                                {/* Mensaje superior */}
                            <div className="space-y-2">
                                <label
                                    htmlFor="store_top_bar_message"
                                    className="text-sm font-medium text-card-foreground"
                                >
                                    Mensaje superior de la tienda
                                </label>

                                <Input
                                    id="store_top_bar_message"
                                    value={form['store.top_bar_message']}
                                    onChange={(e) =>
                                        setForm((current) => ({
                                            ...current,
                                            'store.top_bar_message': e.target.value,
                                        }))
                                    }
                                    placeholder="Entrega a domicilio en 30-45 min · Genéricos de calidad al mejor precio"
                                    disabled={!canEditPlatform}
                                />

                                <p className="text-xs text-muted-foreground">
                                    Este mensaje aparecerá en la barra superior de la tienda en línea.
                                </p>
                            </div>

                                {/* Logos de la tienda */}
                                <div>
                                    <p className="text-sm font-medium text-card-foreground mb-3">Logos de la tienda</p>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {STORE_IMAGE_KEYS.map(({ key, label }) => {
                                            const image = settingMap[key]?.value as ImageSettingValue | null | undefined
                                            const isUploading = uploadingKey === key
                                            return (
                                                <div key={key} className="rounded-lg border border-card-border bg-card p-4">
                                                    <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-card-border bg-card-foreground/5">
                                                        {image?.url ? (
                                                            <img src={image.url} alt={label} className="max-h-20 max-w-full object-contain" />
                                                        ) : (
                                                            <ImageIcon className="h-8 w-8 text-card-description" />
                                                        )}
                                                    </div>
                                                    <div className="mt-3 space-y-2">
                                                        <p className="text-sm font-medium text-card-foreground">{label}</p>
                                                        {canEditPlatform && (
                                                            <Button asChild className="w-full cursor-pointer">
                                                                <label>
                                                                    {isUploading ? (
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <Upload className="h-4 w-4" />
                                                                    )}
                                                                    Subir
                                                                    <input
                                                                        type="file"
                                                                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                                        className="sr-only"
                                                                        disabled={isUploading}
                                                                        onClick={(e) => { e.currentTarget.value = '' }}
                                                                        onChange={(e) => handleImageChange(key as StoreImageKey, e.target.files?.[0])}
                                                                    />
                                                                </label>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <PurchaseBenefitsSettingsCard
                            value={form['store.purchase_benefits']}
                            canEdit={canEditPlatform}
                            onChange={(purchaseBenefits) =>
                                setForm((current) => ({
                                    ...current,
                                    'store.purchase_benefits': purchaseBenefits,
                                }))
                            }
                        />

                        <Card className="rounded-lg">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Globe className="h-5 w-5 text-primary" />
                                    Redes sociales
                                </CardTitle>
                                <CardDescription>Enlaces a tus perfiles en redes sociales.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="grid gap-3 md:grid-cols-2">
                                    {[
                                        { key: 'store.social.facebook' as const, label: 'Facebook', placeholder: 'https://facebook.com/tu-pagina' },
                                        { key: 'store.social.instagram' as const, label: 'Instagram', placeholder: 'https://instagram.com/tu-cuenta' },
                                        { key: 'store.social.tiktok' as const, label: 'TikTok', placeholder: 'https://tiktok.com/@tu-cuenta' },
                                        { key: 'store.social.whatsapp' as const, label: 'WhatsApp', placeholder: 'https://wa.me/521234567890' },
                                    ].map(({ key, label, placeholder }) => (
                                        <div key={key} className="space-y-1">
                                            <label className="text-xs font-medium text-card-foreground">{label}</label>
                                            <Input
                                                value={form[key]}
                                                onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.value }))}
                                                placeholder={placeholder}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {canEditPlatform && (
                            <div className="flex items-center justify-end">
                                <Button type="submit" disabled={updateMutation.isPending || isLoading}>
                                    {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    <Save className="mr-2 h-4 w-4" />
                                    Guardar configuración
                                </Button>
                            </div>
                        )}
                    </form>
                )}
            </div>
        </div>
    )
}
